let _ = t => t,
  _t;
import { getPublicLang } from 'academy-common-lib/utils/url';
import { useState, useEffect } from 'react';
import styled, { useMediaQuery, mediaQuery, css } from 'academy-common-lib/utils/Styling';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import I18n from 'I18n';
import usageTracker from '../../utils/usageTracker';
import styledWithTheme from 'foundations-theming/utils';
import { PlayerPauseIcon } from 'foundations-assets/icons/components/PlayerPauseIcon';
import { PlayerPlayIcon } from 'foundations-assets/icons/components/PlayerPlayIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const beginningsByLang = {
  en: 'Grow',
  de: 'Fördern',
  es: 'Crece',
  pt: 'Cresça',
  fr: 'Accélérez'
};
export const endingsByLang = {
  en: ['Your Career', 'Your Organization', 'Better'],
  de: ['Sie Ihre Karriere', 'Sie Ihr Unternehmen', 'Sie Ihr Wachstum'],
  es: ['como profesional', 'con tu empresa', 'mejor'],
  pt: ['na sua carreira', 'a sua organização', 'melhor'],
  fr: ['votre carrière', 'votre activité', 'votre croissance']
};
const TypeText = styledWithTheme.span(_t || (_t = _`
  white-space: nowrap;
  border-right: 8px solid ${0};
  padding-right: 8px;

  animation: blink 0.75s step-end infinite;
  ${0};

  @keyframes blink {
    50% {
      border-color: transparent;
    }
  }
`), ({
  theme: trellisTheme
}) => trellisTheme.color['do-not-use-great-white'], ({
  $isTypingOrDeleting
}) => $isTypingOrDeleting && 'animation: none');
const HeroHeading = styled.h1.withConfig({
  displayName: "HeroTypingHeading__HeroHeading"
})(["font-size:28px;", ""], mediaQuery('notTablet', css(["font-size:48px;"])));
const HeadingContainer = styled.div.withConfig({
  displayName: "HeroTypingHeading__HeadingContainer"
})(["& button{position:absolute;top:16px;left:calc(50% - 16px);opacity:0;transition:opacity 0.3s;}&:hover button,&:focus-within button{opacity:1;}"]);
const useTypingAnimation = () => {
  const language = getPublicLang();
  const beginning = beginningsByLang[language] || beginningsByLang['en'];
  const endings = endingsByLang[language] || endingsByLang['en'];
  const [isTyping, setIsTyping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [endingsIndex, setEndingsIndex] = useState(0);
  const [endingDisplayText, setEndingDisplayText] = useState(endings[endingsIndex]);
  useEffect(() => {
    // begin animation after 2 seconds
    setTimeout(() => setIsDeleting(true), 2000);
  }, []);
  useEffect(() => {
    if (isStopped) return;
    if (isDeleting && endingDisplayText.length) {
      // not fully deleted
      setTimeout(() => {
        //remove last character
        setEndingDisplayText(endingDisplayText.slice(0, -1));
      }, 100);
    }
    if (isDeleting && !endingDisplayText.length) {
      // fully deleted
      setIsDeleting(false);
      // wait, then start typing
      setTimeout(() => setIsTyping(true), 1000);
      // increment or reset strings index
      setEndingsIndex(endingsIndex === endings.length - 1 ? 0 : endingsIndex + 1);
    }
    if (isTyping && endingDisplayText.length < endings[endingsIndex].length) {
      // not fully typed
      setTimeout(() => {
        //add next character
        setEndingDisplayText(endings[endingsIndex].slice(0, endingDisplayText.length + 1));
      }, 200);
    }
    if (isTyping && endingDisplayText.length === endings[endingsIndex].length) {
      // fully typed
      setIsTyping(false);
      // wait 2 seconds, then start deleting
      setTimeout(() => setIsDeleting(true), 2000);
    }
  }, [isDeleting, endingDisplayText, endingsIndex, isTyping, endings, isStopped]);
  const handlePlayPauseClick = () => {
    setIsStopped(!isStopped);
    usageTracker.track('interaction', {
      action: 'homepage-hero-animation-play-pause-click',
      value: isStopped ? 'play' : 'pause'
    });
  };
  return {
    beginning,
    endings,
    endingDisplayText,
    isTyping,
    isDeleting,
    handlePlayPauseClick,
    isStopped
  };
};
const HeroTypingHeading = () => {
  const {
    isTablet
  } = useMediaQuery();
  const {
    beginning,
    endings,
    endingDisplayText,
    isTyping,
    isDeleting,
    handlePlayPauseClick,
    isStopped
  } = useTypingAnimation();
  return /*#__PURE__*/_jsxs(HeadingContainer, {
    children: [!isTablet && /*#__PURE__*/_jsx(IconButton, {
      "aria-label": I18n.text('academyPublic.homepage.heroSection.playPauseButtonAltText'),
      onClick: handlePlayPauseClick,
      use: "transparent",
      children: isStopped ? /*#__PURE__*/_jsx(PlayerPlayIcon, {}) : /*#__PURE__*/_jsx(PlayerPauseIcon, {})
    }), /*#__PURE__*/_jsx(HeroHeading, {
      children: isTablet || isStopped ? `${beginning} ${endings[0]}` :
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsxs(TypeText, {
        $isTypingOrDeleting: isTyping || isDeleting,
        children: [beginning, " ", endingDisplayText]
      })
    })]
  });
};
export default HeroTypingHeading;